import { getPersistentItem } from '../../vrs-auth/storage';
/**
 * Operating systems may append " #<n>" to a camera label; compare without it.
 *
 * @param {string} label - A camera label.
 * @returns {string}
 */
function stripLabelSuffix(label) {
    return label.replace(/\s#\d*(?!.*\s#\d*)/, '').trim();
}
/**
 * The camera a room should open first, named explicitly.
 *
 * Without an id the browser opens its default camera, which on a machine with
 * two cameras is often not the one the user chose. The id is requested
 * exactly (createLocalTracksF): a bare id is only an ideal, and desktop
 * Chromium does not honour an ideal deviceId when it picks the camera.
 *
 * The portal's choice (vrs_camera_device_id / _label) is what the room applies
 * afterwards (vrs-room-controls.js), so it wins over Jitsi's own settings,
 * which can hold an older choice. Ids must be present in the page's device
 * list; when the list carries no ids (enumerated before camera permission),
 * the saved id is passed as is. If it turns out to be stale, the exact request
 * fails and createLocalTracksWithCameraFallback re-finds the camera by label,
 * then settles for the browser default.
 *
 * @param {IReduxState} state - The redux state (devices and settings).
 * @param {Function} readStored - Reads a VRS persistent key.
 * @returns {string|undefined} A camera id, or undefined for the browser default.
 */
export function resolvePreferredCameraId(state, readStored = getPersistentItem) {
    const devices = state['features/base/devices']?.availableDevices?.videoInput || [];
    const settings = state['features/base/settings'] || {};
    const savedIds = [readStored('vrs_camera_device_id'), settings.userSelectedCameraDeviceId]
        .filter((id) => Boolean(id) && id !== 'default');
    if (!devices.some(device => device.deviceId)) {
        return savedIds[0];
    }
    const byId = savedIds.find(id => devices.some(device => device.deviceId === id));
    if (byId) {
        return byId;
    }
    for (const label of [readStored('vrs_camera_device_label'), settings.userSelectedCameraDeviceLabel]) {
        const match = label ? findCameraIdByLabel(devices, label) : undefined;
        if (match) {
            return match;
        }
    }
    return undefined;
}
/**
 * The label of the camera an id names, so a stale id can be found again.
 * The page's device list knows it when the id is listed; otherwise only a
 * saved choice pairs this exact id with a label.
 *
 * @param {IReduxState} state - The redux state (devices and settings).
 * @param {string} deviceId - The camera id that was requested.
 * @param {Function} readStored - Reads a VRS persistent key.
 * @returns {string|undefined}
 */
export function cameraLabelForId(state, deviceId, readStored = getPersistentItem) {
    const devices = state['features/base/devices']?.availableDevices?.videoInput || [];
    const settings = state['features/base/settings'] || {};
    const listed = devices.find(device => device.deviceId === deviceId)?.label;
    if (listed) {
        return listed;
    }
    if (deviceId === readStored('vrs_camera_device_id')) {
        return readStored('vrs_camera_device_label') || undefined;
    }
    if (deviceId === settings.userSelectedCameraDeviceId) {
        return settings.userSelectedCameraDeviceLabel || undefined;
    }
    return undefined;
}
/**
 * The id a camera with this label has now. Ids change when a camera moves to
 * another USB port or the saved id came from elsewhere; the model name does
 * not.
 *
 * @param {ICameraDevice[]} devices - Cameras (other kinds are skipped).
 * @param {string} label - The label to look for.
 * @param {string} [excludeId] - An id that already failed, never returned.
 * @returns {string|undefined}
 */
export function findCameraIdByLabel(devices, label, excludeId) {
    const wanted = stripLabelSuffix(label);
    return wanted ? devices.find(device => (!device.kind || device.kind === 'videoinput')
        && device.deviceId && device.deviceId !== excludeId
        && device.label && stripLabelSuffix(device.label) === wanted)?.deviceId : undefined;
}
